import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

interface ProductQuantityInputStylesParams {
  isZeroValue?: boolean;
  hasFocusBorder?: boolean;
  isDisabled?: boolean;
  hasError?: boolean;
}

const getQuantitySelectorBorderColor = (
  theme: Theme,
  { isDisabled, hasError }: { isDisabled: boolean; hasError: boolean },
) => {
  if (hasError) {
    return theme.palette.error.main;
  }

  if (isDisabled) {
    return theme.palette.custom.border.secondary;
  }

  return theme.palette.custom.border.primary;
};

export const PRODUCT_STOCK_OVERHANG = 5.5;
export const TOUCH_PRODUCT_STOCK_OVERHANG = 8;

export const useProductQuantityInputStyles = makeStyles<ProductQuantityInputStylesParams | void>()(
  (
    theme: Theme,
    { isZeroValue = false, hasFocusBorder = false, isDisabled = false, hasError = false } = {},
  ) => ({
    quantity: {
      bottom: -PRODUCT_STOCK_OVERHANG,
      position: 'absolute',
      backgroundColor: theme.palette.background.paper,
    },

    touchQuantity: {
      zIndex: 1,
      borderRadius: 4,
      position: 'absolute',
      bottom: -TOUCH_PRODUCT_STOCK_OVERHANG,
      backgroundColor: theme.palette.background.paper,
    },

    textField: {
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.shape.borderRadiusLg,
      '& .MuiOutlinedInput-input': {
        boxSizing: 'border-box',
        height: 32,
        color: isZeroValue ? theme.palette.text.disabled : theme.palette.text.primary,
        padding: theme.spacing(0.25),
        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
          appearance: 'none',
          margin: 0,
        },
        '&:hover::-webkit-inner-spin-button': {
          appearance: 'auto',
        },
      },
      '& .MuiOutlinedInput-root': {
        borderRadius: `${theme.shape.borderRadiusLg}px`,
      },
      '& .MuiInputBase-root.Mui-disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
      },
      '& .MuiInputBase-root.Mui-disabled:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.custom.border.secondary,
      },
      '&& ::placeholder': {
        color: theme.palette.primary.main,
      },
      '&& :disabled': {
        WebkitTextFillColor: theme.palette.text.primary,
        color: theme.palette.text.primary,
      },
    },

    touchTextField: {
      border: 'none',
      verticalAlign: 'inherit',
      backgroundColor: theme.palette.background.paper,
      '& ::before': {
        border: 'none !important',
      },
      '& ::after': {
        border: 'none !important',
      },
      '& .MuiInputBase-root.Mui-disabled': {
        backgroundColor: 'inherit',
      },
      '& .MuiInputBase-input': {
        MozAppearance: 'textfield',
        textAlign: 'center',
        fontSize: theme.typography.body1.fontSize,
      },
      '& .MuiInputBase-input::-webkit-outer-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
      '& .MuiInputBase-input::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
      '& .MuiInputBase-root.MuiInputBase-sizeSmall > .MuiInputBase-input': {
        padding: 0,
      },
      '&& ::placeholder': {
        color: theme.palette.primary.main,
      },
      '&& :disabled': {
        WebkitTextFillColor: theme.palette.text.primary,
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.action.disabledBackground,
      },
    },

    quantitySelector: {
      border: `1px solid ${getQuantitySelectorBorderColor(theme, { isDisabled, hasError })}`,
      borderRadius: theme.shape.borderRadius,
      position: 'relative',
      height: 80,
      width: '100%',
      ...(hasFocusBorder && {
        '&:focus-within': {
          borderColor: theme.palette.text.primary,
        },
      }),
    },

    addBorderRadius: {
      borderTopLeftRadius: theme.spacing(1),
      borderTopRightRadius: theme.spacing(1),
      '&.Mui-disabled': {
        borderTopLeftRadius: theme.spacing(1),
        borderTopRightRadius: theme.spacing(1),
      },
    },

    subtractBorderRadius: {
      borderBottomLeftRadius: theme.spacing(1),
      borderBottomRightRadius: theme.spacing(1),
      '&.Mui-disabled': {
        borderBottomLeftRadius: theme.spacing(1),
        borderBottomRightRadius: theme.spacing(1),
      },
    },

    addOrSubtract: {
      width: '100%',
      height: 28,
      backgroundColor: theme.palette.background.paper,
      '&.Mui-disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
      },
    },
  }),
);
